import React, { useState, useEffect } from "react";
import api from "../api"; 
import { FaInfoCircle } from "react-icons/fa";
import { toast } from 'react-toastify'; 

const ApproveBeneficiaryListPage = ({ role, projectId }) => {
    const [beneficiaries, setBeneficiaries] = useState([]);
    const [selected, setSelected] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [message, setMessage] = useState("");
    const [error, setError] = useState("");
    const departmentId = localStorage.getItem("departmentId");

    useEffect(() => {
        const fetchBeneficiaries = async () => {
            if (!projectId) {
                setError("No project has been selected.");
                setBeneficiaries([]);
                return;
            }
             if (!departmentId) {
                setError("Department ID not found. Please re-select project.");
                setBeneficiaries([]);
                return;
            }

            setIsLoading(true);
            setError("");
            setMessage(""); 
            try {
                const response = await api.get(
                    `/vres/projects/${projectId}/departments/${departmentId}/beneficiaries?status=pending_approval`
                );
                setBeneficiaries(response.data || []); 
            } catch (err) {
                const errorMessage =
                    err.response?.data?.message ||
                    err.message ||
                    "Failed to fetch beneficiaries.";
                if (err.response?.status === 403) {
                    setError("You are not authorized to view this page. Please log in again.");
                } else {
                    setError(errorMessage);
                }
                setBeneficiaries([]); 
            } finally {
                setIsLoading(false);
            }
        };

        fetchBeneficiaries();
    }, [projectId, departmentId]);

    // --- NEW: Select All Logic ---
    const handleSelectAll = (e) => {
        if (e.target.checked) {
            // Select all beneficiary IDs
            const allIds = beneficiaries.map(b => b.beneficiaryId);
            setSelected(allIds);
        } else {
            // Clear selection
            setSelected([]);
        }
    };

    const handleCheckboxChange = (beneficiaryId) => {
        setSelected((prev) =>
            prev.includes(beneficiaryId)
                ? prev.filter((id) => id !== beneficiaryId)
                : [...prev, beneficiaryId]
        );
    };

    const handleStatusUpdate = async (newStatus) => {
        if (selected.length === 0) {
            const selectErrorMsg = `No beneficiaries selected to ${newStatus}.`;
            setError(selectErrorMsg);
            toast.error(selectErrorMsg); 
            return;
        }

        setIsLoading(true);
        setMessage("");
        setError("");

        try {
            const response = await api.put(
                "/vres/beneficiaries/status", 
                {
                    beneficiaryIds: selected,
                    status: newStatus,
                }
            );

            const successMsg = response.data.message || `Successfully updated status for ${selected.length} beneficiaries.`;
            setMessage(successMsg);
            toast.success(successMsg); 

            setBeneficiaries(
                beneficiaries.filter((b) => !selected.includes(b.beneficiaryId))
            );
            setSelected([]); 
        } catch (err) {
            console.error("Status update error:", err); 

            let displayMessage = `Failed to ${newStatus} beneficiaries. Please try again.`; 

            if (err.response && err.response.data) {
                const backendData = err.response.data;
                let backendMessage = "";

                 if (backendData && typeof backendData === 'object' && backendData.message) {
                    backendMessage = backendData.message;
                } else if (typeof backendData === 'string') {
                    backendMessage = backendData;
                }

                if (backendMessage) {
                    displayMessage = `Update failed: ${backendMessage}`;
                } else if (err.response.status === 403) {
                     displayMessage = "Update failed: You do not have permission.";
                } else {
                    displayMessage = `Update failed: Request failed with status code ${err.response.status}.`;
                }
            } else if (err.message) {
                displayMessage = `Update failed: ${err.message}`;
            }

            setError(displayMessage); 
            toast.error(displayMessage); 

        } finally {
            setIsLoading(false);
        }
    };

    const canApproveReject = role === "checker"; 

    // Helper to check if all rows are selected
    const isAllSelected = beneficiaries.length > 0 && selected.length === beneficiaries.length;

    return (
        <div style={styles.container}>
            <h2 style={styles.heading}>Approve Beneficiary List</h2>
            
            {error && (
                <div style={{ color: "red", marginBottom: "15px", textAlign: 'center', fontWeight: 'bold' }}>{error}</div>
            )}
            {message && (
                <div style={{ color: "green", marginBottom: "15px", textAlign: 'center', fontWeight: 'bold' }}>{message}</div>
            )}

            {isLoading && <p>Loading beneficiaries...</p>}

            {!isLoading && beneficiaries.length === 0 && !error && (
                <p>
                    No beneficiaries are currently awaiting approval for this project department.
                </p>
            )}

            {!isLoading && beneficiaries.length > 0 && (
                <form>
                    <table style={styles.table}>
                        <thead>
                            <tr>
                                <th style={styles.tableHeader}>
                                    {/* --- Select All Checkbox --- */}
                                    <input 
                                        type="checkbox" 
                                        onChange={handleSelectAll}
                                        checked={isAllSelected}
                                        disabled={!canApproveReject || isLoading}
                                        style={{ cursor: 'pointer' }}
                                    />
                                    <span style={{ marginLeft: '8px', fontSize: '0.9em' }}>Select All</span>
                                </th>
                                <th style={styles.tableHeader}>Name</th>
                                <th style={styles.tableHeader}>Phone</th>
                                <th style={styles.tableHeader}>Status</th>
                            </tr>
                        </thead>
                        <tbody>
                            {beneficiaries.map((b) => (
                                <tr key={b.beneficiaryId}>
                                    <td style={styles.tableCell}>
                                        <input
                                            type="checkbox"
                                            checked={selected.includes(b.beneficiaryId)}
                                            disabled={!canApproveReject || isLoading}
                                            onChange={() => handleCheckboxChange(b.beneficiaryId)}
                                        />
                                    </td>
                                    <td style={styles.tableCell}>{b.name}</td>
                                    <td style={styles.tableCell}>{b.phone}</td>
                                    <td style={styles.tableCell}>{b.status}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>

                    {canApproveReject && (
                        <div style={styles.buttonGroup}>
                            <button
                                type="button"
                                style={styles.primaryBtn}
                                onClick={() => handleStatusUpdate("active")} 
                                disabled={isLoading || selected.length === 0}
                            >
                                {isLoading ? "Processing..." : `Approve Selected (${selected.length})`}
                            </button>
                        </div>
                    )}
                </form>
            )}
        </div>
    );
};

export default ApproveBeneficiaryListPage;

const styles = {
    container: {
        backgroundColor: "#ffffff",
        padding: "30px",
        maxWidth: "900px", 
        margin: "40px auto",
        borderRadius: "8px",
        boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
        fontFamily: "Arial, sans-serif",
        color: "#343a40",
    },
    heading: {
        textAlign: "center",
        color: "#007bff",
        marginBottom: "24px",
    },
    table: {
        width: "100%",
        borderCollapse: "collapse",
        marginBottom: "20px",
        textAlign: "left",
    },
     tableHeader: { 
        borderBottom: "2px solid #dee2e6",
        padding: "12px",
        backgroundColor: '#f8f9fa'
    },
     tableCell: { 
        borderBottom: "1px solid #dee2e6",
        padding: "12px",
    },
    buttonGroup: {
        display: "flex",
        justifyContent: "flex-end",
        gap: "10px",
        marginTop: "20px",
    },
    primaryBtn: {
        padding: "10px 20px",
        backgroundColor: "#28a745", 
        color: "white",
        border: "none",
        borderRadius: "4px",
        cursor: "pointer",
        fontWeight: "bold",
    },
    secondaryBtn: {
        padding: "10px 20px",
        backgroundColor: "#dc3545", 
        color: "white",
        border: "none",
        borderRadius: "4px",
        cursor: "pointer",
        fontWeight: "bold",
    },
};